import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { useQueryClient } from "react-query";
import { useIntercom } from "react-use-intercom";

import ProfileBar from "../../Organisms/App Utility Bar/profilebar";

import renderCreditBuilderOptIn from "../../Organisms/Modals/Credit-Builder/credit-builder-opt-in";
import renderAlertConfirmationModal from "../../Organisms/Modals/alert-confirmation-modal";
import renderManagePaymentMethods from "../../Organisms/Modals/Payment Methods/manage-payment-methods";
import renderMyUtilitiesUpsell from "../../Organisms/Modals/MyUtilities/upsell-modal";
import renderApplyInsurance from "../../Organisms/Modals/Insurance/apply-insurance";
import renderApplyPublicGrid from "../../Organisms/Modals/PublicGrid/apply-publicgrid";

import useStakeCheckingStatusAction from "../../CustomHooks/useStakeCheckingStatusAction";

import { useModalContext } from "../../Contexts/ModalContext";

import { useConsumerDeals, useDeals, useStakeDebit } from "../../CustomHooks/Queries";

import {
  dispatchErrorToast,
  dispatchSuccessToast,
  formatCurrency,
  isSVDDenied,
  isSVDVerificationRequired,
} from "../../Utils/helpers";
import { APIQueryKeys, UpdateDealPaymentModalProps } from "../../Utils/constants";

import { CelebrationConfetti } from "../../Assets/Icons";
import CloseButton from "../../Components/Buttons/close-button";

import "./deals.scss";
import "./deals-responsive.scss";

export const useConsumerDealsData = () => {
  const consumerDealsAPI = useConsumerDeals();

  const {
    isLoading: isLoadingConsumerDeals,
    isFetching: isFetchingConsumerDeals,
    data: consumerDealsData,
  } = consumerDealsAPI;

  const offersProps = useMemo(() => {
    const offers = consumerDealsData?.data?.data?.offers ?? [];

    const stakeSaverOffer = offers.find((offer) => offer.id === "stake_saver_bundle");
    const stakeCheckingOffer = offers.find((offer) => offer.id === "stake_checking_account");
    const creditBuilderOffer = offers.find((offer) => offer.id === "credit_builder");
    const insuranceOffer = offers.find((offer) => offer.id === "stake_partner_insurance");
    const myUtilitiesOffer = offers.find((offer) => offer.id === "stake_partner_utilities");
    const publicGridOffer = offers.find((offer) => offer.id === "stake_partner_publicgrid");
    const neighborhoodOffer = offers.find((offer) => offer.id === "neighborhood_network");


    const isStakeSaverActive = stakeSaverOffer?.is_activated ?? false;
    const isStakeCheckingActive = stakeCheckingOffer?.is_activated ?? false;
    const isCreditBuilderActive = creditBuilderOffer?.is_activated ?? false;
    const isInsuranceActive = insuranceOffer?.is_activated ?? false;
    const isMyUtilitiesActive = myUtilitiesOffer?.is_activated ?? false;
    const isPublicGridActive = publicGridOffer?.is_activated ?? false;
    const isPublicGridConsented = publicGridOffer?.is_consented ?? false;
    const isNeighborhoodActive = neighborhoodOffer?.is_activated ?? false;


    return {
      offers,
      stakeSaverOffer,
      stakeCheckingOffer,
      creditBuilderOffer,
      insuranceOffer,
      myUtilitiesOffer,
      publicGridOffer,
      isStakeSaverActive,
      isStakeCheckingActive,
      isCreditBuilderActive,
      isInsuranceActive,
      isMyUtilitiesActive,
      isPublicGridActive,
      isPublicGridConsented,
      isNeighborhoodActive
    };
  }, [consumerDealsData]);

  return {
    isLoadingConsumerDealsData: isLoadingConsumerDeals,
    isFetchingConsumerDealsData: isFetchingConsumerDeals,
    consumerDealsAPI,
    ...offersProps,
  };
};

export const useStakeSaverDealAction = () => {
  const navigate = useNavigate();

  return () => {
    navigate("/deals/stake-saver");
  };
};

export const useStakeCheckingDealAction = (isStakeCheckingActive) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { show: showIntercom } = useIntercom();

  const stakeCheckingStatusAction = useStakeCheckingStatusAction();

  const { data: stakeDebit } = useStakeDebit();

  return useCallback(
    () => {
      if (isStakeCheckingActive) {
        navigate("/manage-stake-debit");
      } else {
        if (isSVDVerificationRequired(stakeDebit?.data)) {
          navigate("/verify-stake-checking", { state: { previousPathname: pathname } });
        } else if (isSVDDenied(stakeDebit?.data)) {
          showIntercom();
        } else {
          stakeCheckingStatusAction(stakeDebit?.data);
        }
      }
    }, //eslint-disable-next-line
    [isStakeCheckingActive, stakeDebit, stakeCheckingStatusAction],
  );
};

export const useCreditBuilderDealAction = (isCreditBuilderActive) => {
  const modalContext = useModalContext();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useCallback(
    () => {
      if (isCreditBuilderActive) {
        navigate("/deals/credit-builder");
      } else {
        renderCreditBuilderOptIn(modalContext, {
          onOptIn: () => queryClient.refetchQueries(APIQueryKeys.consumerDeals),
        });
      }
    }, //eslint-disable-next-line
    [isCreditBuilderActive, modalContext],
  );
};

export const useInsuranceDealAction = (isInsuranceActive) => {
  const navigate = useNavigate();

  const modalContext = useModalContext();

  return useCallback(() => {
    if (isInsuranceActive) {
      navigate("/deals/insurance/manage");
    } else {
      renderApplyInsurance(modalContext);
    }
  }, [isInsuranceActive, modalContext, navigate]);
};

export const useMyUtilitiesDealAction = (isActive) => {
  const modalContext = useModalContext();
  const navigate = useNavigate();

  return useCallback(() => {
    if (isActive) {
      navigate("/deals/my-utilities/manage");
    } else {
      renderMyUtilitiesUpsell(modalContext);
    }
  }, [modalContext, isActive, navigate]);
};



export const usePublicGridDealAction = () => {
  const modalContext = useModalContext();

  return useCallback(() => {
    renderApplyPublicGrid(modalContext);
  }, [modalContext]);
};

export const useNeighbourhoodAction = (isNeighborhoodActive) => {
  const navigate = useNavigate();

  return useCallback(() => {
      navigate("/deals/neighborhood-network");
    
  }, [isNeighborhoodActive, navigate]);
};

function ChevronIcon() {
  return (
    <svg className="chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M6 4l4 4-4 4" stroke="#8B8FA3" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const DealRow = ({ icon, title, description, image_link, is_activated, status_display_text, onButtonClick }) => {
  const badgeText = status_display_text || (is_activated ? "Active" : null);

  return (
    <div className="deals-row" onClick={onButtonClick} role="button" tabIndex={0}>
      <div className="deals-row-icon">
        {image_link ? <img src={image_link} alt="" /> : icon}
      </div>
      <div className="deals-row-text">
        <div className="deals-row-title-line">
          <span className="deals-row-title">{title}</span>
          {badgeText && <span className="deals-badge">{badgeText}</span>}
        </div>
        {description && <span className="deals-row-sub">{description}</span>}
      </div>
      <ChevronIcon />
    </div>
  );
};

export default function Deals() {
  const [dealPaymentFailure, setDealPaymentFailure] = useState(null);
  const [UMFRebateInfo, setUMFRebateInfo] = useState(null);
  const [stakeSaverDiscount, setStakeSaverDiscount] = useState(null);

  const modalContext = useModalContext();
  const { onModalClose } = modalContext;

  const { isLoading: isLoadingStakeDebit } = useStakeDebit();

  const { isLoading: isLoadingDealsApi } = useDeals();

  const {
    isLoadingConsumerDealsData,
    offers,
    stakeSaverOffer,
    isStakeCheckingActive,
    isCreditBuilderActive,
    isInsuranceActive,
    isMyUtilitiesActive,
    isNeighborhoodActive,
    consumerDealsAPI: { data: consumerDealsAPIData },
  } = useConsumerDealsData();

  useEffect(() => {
    if (stakeSaverOffer?.offers_notification?.is_payment_failed) {
      const {
        offer_name,
        id,
        offers_notification,
        check_out_details: { selected_payment_method },
      } = stakeSaverOffer;
      setDealPaymentFailure({ offer_name, id, selected_payment_method, ...offers_notification });
    }
  }, [stakeSaverOffer]);

  useEffect(() => {
    if (consumerDealsAPIData?.data?.data?.rebate_information?.is_rebate_not_available) {
      setUMFRebateInfo(consumerDealsAPIData?.data?.data?.rebate_information);
    }
  }, [consumerDealsAPIData?.data?.data?.rebate_information]);

  useEffect(() => {
    if (stakeSaverOffer?.is_activated && !!stakeSaverOffer?.check_out_details?.activatedFrequency?.[0]?.discount) {
      setStakeSaverDiscount(stakeSaverOffer?.check_out_details?.activatedFrequency?.[0]?.discount);
    }
  }, [stakeSaverOffer]);

  const StakeSaverDealAction = useStakeSaverDealAction();
  const StakeCheckingDealAction = useStakeCheckingDealAction(isStakeCheckingActive);
  const CreditBuilderDealAction = useCreditBuilderDealAction(isCreditBuilderActive);
  const InsuranceDealAction = useInsuranceDealAction(isInsuranceActive);
  const MyUtilitiesDealAction = useMyUtilitiesDealAction(isMyUtilitiesActive);
  const PublicGridDealAction = usePublicGridDealAction();
  const NeighborhoodAction = useNeighbourhoodAction(isNeighborhoodActive)

  const attributesInjectedOffers = useMemo(() => {
    const getOfferAction = (offer) => {
      switch (offer.id) {
        case "stake_saver_bundle":
          return StakeSaverDealAction;
        case "stake_checking_account":
          return StakeCheckingDealAction;
        case "credit_builder":
          return CreditBuilderDealAction;
        case "stake_partner_insurance":
          return InsuranceDealAction;
        case "stake_partner_utilities":
          return MyUtilitiesDealAction;
        case "stake_partner_publicgrid":
          return PublicGridDealAction;
        case "neighborhood_network":
          return NeighborhoodAction
        default:
          return () => {};
      }
    };

    const copyOverrides = {
      stake_saver_bundle: {
        title: "Stake Saver",
        description: "Cash back, credit building, and early paycheck — from $10/mo",
      },
      stake_checking_account: {
        description: "Earning cash back on rent and purchases",
      },
      stake_partner_utilities: {
        title: "Utilities & more",
        description: "Exclusive rates on internet, TV, and utilities.",
      },
      stake_partner_publicgrid: {
        title: "Public Grid",
        description: "Set up or manage your electricity account.",
      },
    };

    return offers.map((offer) => ({
      ...offer,
      ...copyOverrides[offer.id],
      onButtonClick: getOfferAction(offer),
    }));
  }, [
    offers,
    StakeSaverDealAction,
    StakeCheckingDealAction,
    CreditBuilderDealAction,
    InsuranceDealAction,
    MyUtilitiesDealAction,
    PublicGridDealAction,
    NeighborhoodAction
  ]);

  const deals = useMemo(
    () => attributesInjectedOffers.filter((offer) => !offer.is_activated),
    [attributesInjectedOffers],
  );

  const activatedDeals = useMemo(
    () => attributesInjectedOffers.filter((offer) => offer.is_activated && offer.id),
    [attributesInjectedOffers],
  );

  const isLoading = isLoadingConsumerDealsData || isLoadingStakeDebit || isLoadingDealsApi;

  const [animReady, setAnimReady] = useState(false);
  const hasTriggeredAnim = useRef(false);

  useEffect(() => {
    if (!isLoading && !hasTriggeredAnim.current) {
      hasTriggeredAnim.current = true;
      requestAnimationFrame(() => setAnimReady(true));
    }
  }, [isLoading]);

  const handleUpdatePayment = () => {
    renderAlertConfirmationModal(modalContext, {
      ...UpdateDealPaymentModalProps,
      description: dealPaymentFailure?.payment_failure_description,
      onCTAButtonClick: () =>
        renderManagePaymentMethods(modalContext, {
          selectedPaymentMethod: dealPaymentFailure?.selected_payment_method,
          mustUpdatePaymentMethod: true,
          chargePaymentImmediately: true,
          dealId: dealPaymentFailure?.id,
        }),
      onSecondaryCTAButtonClick: onModalClose,
    });
  };

  return (
    <div className={`deals${animReady ? " anim-ready" : ""}`}>
      <section>
        <div className="max-width-container">
          <ProfileBar />
          {isLoading && (
            <div className="loading-content">
              <CircularProgress />
            </div>
          )}
          {!isLoading && (
            <>
              {!!dealPaymentFailure && (
                <div className="deal-banner alert">
                  <div className="d-flex align-items-start justify-content-between gap-3">
                    <h4 className="title">Payment Failed</h4>
                    <CloseButton onClick={setDealPaymentFailure.bind(this, null)} />
                  </div>
                  <p className="description">Update your payment method to keep {dealPaymentFailure?.offer_name}.</p>
                  <button className="action-button" onClick={handleUpdatePayment}>
                    Update Now
                  </button>
                </div>
              )}
              {!!UMFRebateInfo && (
                <div className="deal-banner success">
                  <div className="d-flex align-items-start justify-content-between gap-3">
                    <h4 className="title">{UMFRebateInfo?.rebate_not_available_msg}</h4>
                  </div>
                </div>
              )}
              {!!stakeSaverDiscount && (
                <div className="deal-banner success">
                  <div className="d-flex align-items-start gap-4">
                    <CelebrationConfetti className="banner-icon" />
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-start justify-content-between gap-3">
                        <h4 className="title">
                          You just saved {formatCurrency(stakeSaverDiscount).replace(".00", "")} by joining Stake Saver!
                        </h4>
                        <CloseButton onClick={setStakeSaverDiscount.bind(this, null)} />
                      </div>
                      <p className="description mb-0">Keep earning rewards and building credit.</p>
                    </div>
                  </div>
                </div>
              )}
              {deals.length > 0 && (
                <div className="deals-section">
                  <div className="deals-section-header">
                    <span>Recommended for you</span>
                  </div>
                  {deals.map((deal, idx) => (
                    <DealRow key={idx} {...deal} />
                  ))}
                </div>
              )}
              {activatedDeals.length > 0 && (
                <div className="deals-section">
                  <div className="deals-section-header">
                    <span>Activated</span>
                  </div>
                  {activatedDeals.map((deal, idx) => (
                    <DealRow key={idx} {...deal} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
